import { Api } from '@/api/api';
import { ClientDuplicateRequestFilters } from '@/api/requests/clients/client-duplicate-request-filters';
import { ClientPartnerRequestFilters } from '@/api/requests/clients/client-partner-request-filters';
import { ClientRequestFilters } from '@/api/requests/clients/client-request-filters';
import { CreateClientRequest } from '@/api/requests/clients/create-client-request';
import { UpdateClientRequest } from '@/api/requests/clients/update-client-request';
import { UpdateClientWillHistoryRequest } from '@/api/requests/clients/update-client-will-history-request';
import { ClientsResponse } from '@/api/responses/clients/clients-response';
import { Appointment } from '@/shared/appointments/appointment';
import { ClientAccount } from '@/shared/clients/client-account';
import { ClientContactHistory } from '@/shared/clients/client-account-contact-history';
import { ClientAccountDuplicate } from '@/shared/clients/client-account-duplicate';
import { ClientAccountNote } from '@/shared/clients/client-account-note';
import { ClientAccountWill } from '@/shared/clients/client-account-will';

export class ClientAccountsApi extends Api {
    public getClientAccountsAsync(filters: ClientRequestFilters | null = null): Promise<ClientsResponse> {
        return this.getAsync<ClientsResponse>(`v1/client`, filters);
    }

    public getClientAccountByIdAsync(id: string): Promise<ClientAccount> {
        return this.getAsync<ClientAccount>(`v1/client/${id}`);
    }

    public getClientAccountByAccountNumberAsync(accountNo: number): Promise<ClientAccount> {
        return this.getAsync<ClientAccount>(`v1/client/account/${accountNo}`);
    }

    public async createClientAccountAsync(client: CreateClientRequest): Promise<string> {
        const url = this.buildUrl(`v1/client`);
        const location = await this.postAsync<string>(`v1/client`, client);
        return location.replace(`${url}/`, ``);
    }

    public updateClientAccountAsync(client: ClientAccount): Promise<string> {
        const request: UpdateClientRequest = {
            ...client,
            factFind: client.factFind == null ? null : { ...client.factFind },
            will: client.will == null
                ? null
                : {
                    ...client.will,
                    executors: [...client.will.executors!],
                    guardians: [...client.will.guardians!],
                },
            powerOfAttorneys: [...client.powerOfAttorneys!],
            additionalDocuments: [...client.additionalDocuments!],
            deeds: [...client.deeds!],
        };
        return this.putAsync<string>(`v1/client/${client.id}`, request);
    }

    public async deleteClientAsync(client: ClientAccount): Promise<void> {
        await this.deleteAsync(`v1/client/${client.id}`);
    }

    public getDuplicatesAsync(filters: ClientDuplicateRequestFilters): Promise<Array<ClientAccountDuplicate>> {
        return this.getAsync<Array<ClientAccountDuplicate>>(`v1/client/duplicate`, filters);
    }

    public getClientPotentialPartnersAsync(id: string, filters: ClientPartnerRequestFilters): Promise<ClientsResponse> {
        return this.getAsync<ClientsResponse>(`v1/client/${id}/partner`, filters);
    }

    public async assignClientPartnerAsync(client: ClientAccount, partnerId: string): Promise<void> {
        await this.postAsync(`v1/client/${client.id}/partner`, { partnerId, version: client.version });
    }

    public async removeClientPartnerAsync(clientId: string, partnerId: string): Promise<void> {
        await this.deleteAsync(`v1/client/${clientId}/partner/${partnerId}`);
    }

    public async getClientWillHistory(clientId: string): Promise<Array<ClientAccountWill>> {
        return this.getAsync<Array<ClientAccountWill>>(`v1/client/${clientId}/will/history`);
    }

    public async updateClientWillHistory(clientId: string, will: ClientAccountWill): Promise<string> {
        const request: UpdateClientWillHistoryRequest = {
            ...will,
            id: will.id!,
            executors: [...will.executors],
            guardians: [...will.guardians],
        };
        return this.putAsync<string>(`v1/client/${clientId}/will/history/${will.id!}`, request);
    }

    public async deleteClientWillHistory(clientId: string, willId: string): Promise<void> {
        return this.deleteAsync<void>(`v1/client/${clientId}/will/history/${willId}`);
    }

    public async getClientNotesAsync(clientId: string): Promise<Array<ClientAccountNote>> {
        return this.getAsync<Array<ClientAccountNote>>(`v1/client/${clientId}/note`);
    }

    public async createClientNoteAsync(clientId: string, note: ClientAccountNote): Promise<string> {
        const url = this.buildUrl(`v1/client/${clientId}/note`);
        const location = await this.postAsync<string>(`v1/client/${clientId}/note`, { ...note });
        return location.replace(`${url}/`, ``);
    }

    public async updateClientNoteAsync(clientId: string, note: ClientAccountNote): Promise<string> {
        const url = this.buildUrl(`v1/client/${clientId}/note/${note.id!}`);
        const location = await this.putAsync<string>(`v1/client/${clientId}/note/${note.id!}`, { ...note });
        return location.replace(`${url}/`, ``);
    }

    public async deleteClientNoteAsync(clientId: string, note: ClientAccountNote): Promise<void> {
        await this.deleteAsync(`v1/client/${clientId}/note/${note.id!}`);
    }

    public async getClientContactHistoryAsync(clientId: string): Promise<ClientContactHistory> {
        return this.getAsync<ClientContactHistory>(`v1/client/${clientId}/contact`);
    }

    public async getClientAppointmentBookingsAsync(clientId: string): Promise<Array<Appointment>> {
        return this.getAsync<Array<Appointment>>(`v1/client/${clientId}/appointment`);
    }

    public constructor() {
        super();
    }
}
